import { del, get, post, put, throwIfError } from 'utils/api';
import { VEHICLES } from 'utils/constants/household';
import HOUSEHOLD_DESIGNATION_CODES from 'constants/householdDesignationCodes';

export const postHousehold = async (
  organizationId,
  propertyId,
  portalUserId,
  householdType,
  options,
) => {
  const response = await post(
    `/portal/${organizationId}/${propertyId}/create-application/${portalUserId}`,
    { householdType },
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const postHouseholdV2 = async (
  organizationId,
  propertyId,
  portalUserId,
  householdType,
  options,
) => {
  const response = await post(
    `/portal/${organizationId}/${propertyId}/create-application/${portalUserId}`,
    { householdType },
    options,
  );

  return throwIfError(response);
};

export const postCreateAffordaPortalHousehold = async ({
  organizationId,
  propertyId,
  portalUserId,
  householdType,
  data,
  options,
}) => {
  const response = await post(
    `/portal/${organizationId}/${propertyId}/create-affordaportal-application/${portalUserId}`,
    { householdType, data },
    options,
  );

  return throwIfError(response);
};

export const getHouseholdSummary = async (
  organizationId,
  propertyId,
  householdId,
  options,
) => {
  const url = `/portal/${organizationId}/${propertyId}/households/${householdId}`;
  const response = await get(url, options);

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getHouseholdMembers = async (
  organizationId,
  propertyId,
  householdId,
  options,
) => {
  const url = `/portal/${organizationId}/${propertyId}/all-household-members/${householdId}`;
  const response = await get(url, options);

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getHouseholdMembersEligibility = async (
  organizationId,
  propertyId,
  householdId,
  affordableProgramId,
  options,
) => {
  // https://api-dev.fortress.engineering/{organizationId}/{propertyId}/household-profile/{householdId}/property-affordable-programs/{affordableProgramId}/qualifications

  const url = `/${organizationId}/${propertyId}/household-profile/${householdId}/property-affordable-programs/${affordableProgramId}/qualifications`;
  const response = await get(url, options);

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getResidentPets = async (
  organizationId,
  propertyId,
  householdId,
  options,
) => {
  const response = await get(
    `/${organizationId}/${propertyId}/households/${householdId}/residents/pets`,
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getHouseholdApplicationFees = async (
  organizationId,
  propertyId,
  householdId,
  options = {},
) => {
  const response = await get(
    `/${organizationId}/${propertyId}/application-fees?householdId=${householdId}`,
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getHouseholdBalance = async (
  organizationId,
  propertyId,
  householdId,
  options = {},
) => {
  const response = await get(
    `/portal/${organizationId}/${propertyId}/households/${householdId}/balance`,
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getHouseholdBalanceDetails = async (
  { organizationId, propertyId, householdId, filter, page, limit },
  options = {},
) => {
  const response = await get(
    `/portal/${organizationId}/${propertyId}/households/${householdId}/balance/details?filter=${filter}&page=${
      page || 1
    }&limit=${limit || 10}`,
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getHouseholdUnit = async (
  organizationId,
  propertyId,
  householdId,
  options = {},
) => {
  const response = await get(
    `/portal/${organizationId}/${propertyId}/households/${householdId}/unit`,
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const postHouseholdApplicationFees = async (
  organizationId,
  propertyId,
  householdId,
) => {
  const response = await post(
    `/portal/${organizationId}/${propertyId}/households/${householdId}/postApplicationFees`,
    {},
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const postHouseholdApplicationFeesV2 = async (
  organizationId,
  propertyId,
  householdId,
) => {
  const response = await post(
    `/portal/${organizationId}/${propertyId}/households/${householdId}/postApplicationFees`,
    {},
  );

  return throwIfError(response);
};

export const getPropertyPetFees = async (
  organizationId,
  propertyId,
  options,
) => {
  const response = await get(
    `/${organizationId}/properties/${propertyId}/petFees`,
    options,
  );

  if (response.error) {
    return response.error;
  }

  return response;
};

export const getVehicleModels = async (makeId, options) =>
  get(`/vehicle-models?makeId=${makeId}`, options);

export const getHouseholdUserFriendlyId = async (householdId) =>
  get(`/households/${householdId}/user-friendly-id`);

/**
 * Util that returns a POST, GET, PUT, or DELETE service function for Leaseholders, minors, pets, or vehicles.
 * @param {function} method Takes one of the imported http methods
 * @returns {function} CRUD service function
 *
 * The returned function takes the following parameters:
 * @param {string} organizationId
 * @param {string} propertyId
 * @param {string} householdId
 * @param {string} type string value that must be `leaseholders`, `minors`, `pets`, `vehicles`, `emergency-contacts`
 * @param {string} id ID if not doing a POST: `customerId`, `applicantMinorId`, `applicantPetId`, `vehicleId`, `emergencyContactId
 * @param {object | null} values values to be saved in the records of the new household member
 * @returns { response | error }
 */
const householdMemberAction =
  (method) =>
  async (organizationId, propertyId, householdId, type, id, values = null) => {
    if (
      values?.householdDesignation ===
      HOUSEHOLD_DESIGNATION_CODES.LIVE_IN_CARETAKER
    ) {
      const url = `/${organizationId}/${propertyId}/applications/${values.applicationId}/applicants`;

      const payload = { ...values };
      delete payload.applicationId;
      delete payload.householdDesignation;
      delete payload.client;

      const response = await method(url, payload);
      if (response.error) {
        return response.error;
      }
      return response;
    }
    const updateId = id ? `/${id}` : '';
    // TODO: Remove ternary
    const portalPrepend = type !== VEHICLES ? '/portal' : '';
    const url = `${portalPrepend}/${organizationId}/${propertyId}/households/${householdId}/${type}${updateId}`;
    const response = values ? await method(url, values) : await method(url);

    if (response.error) {
      return response.error;
    }
    return response;
  };

/**
 * Crud services generated by the `householdMemberAction` util
 */
export const addHouseholdMember = householdMemberAction(post);
export const getHouseholdMember = householdMemberAction(get);
export const updateHouseholdMember = householdMemberAction(put);
export const deleteHouseholdMember = householdMemberAction(del);

export const getHotmaStatus = async (
  { programName, state, effectiveDate },
  options = {},
) => {
  const response = await get(
    `/stateAffordableProgramConfig/determineHotmaStatus?programName=${programName}&effectiveDate=${effectiveDate}&state=${state}`,
    options,
  );

  if (response.error) {
    return response.error;
  }
  return response;
};

export const getApplicationInfo = async ({
  organizationId,
  propertyId,
  applicationId,
  options,
}) => {
  //https://api-dev.fortress.engineering/e031e25a-2c69-4cb4-ad95-543ae0cbc9a9/496f0b45-98d2-4ab2-84aa-2e7e4faa42cf/applications/1598d22d-0370-4c8a-b6ad-a46325d5235e

  const url = `/${organizationId}/${propertyId}/applications/${applicationId}`;

  const response = await get(url, options);

  if (response.error) {
    return response.error;
  }
  return response;
};

export const getAffordableConfigs = async (
  { organizationId, propertyId, date },
  options = {},
) => {
  const response = await get(
    `/${organizationId}/${propertyId}/affordable-configs?date=${date}`,
    options,
  );
  if (response.error) {
    return response.error;
  }
  return response;
};
